import client from './client';

export interface OrganizationMembership {
  organization_id: string;
  organization_name: string;
  organization_title: string | null;
  /** "org_admin" or "member" */
  role: string;
  is_primary: boolean;
}

export interface Organization {
  id: string;
  name: string;
  title: string | null;
  is_active: boolean;
}

export interface OrgMember {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_primary: boolean;
}

/** Orgs the signed-in user can switch into. Includes their primary org,
 * any extra memberships, and (for super-admins) every active org. */
export async function getMyMemberships(): Promise<OrganizationMembership[]> {
  const r = await client.get<OrganizationMembership[]>('/organizations/me/memberships');
  return r.data;
}

/** All orgs in the deployment — super-admin only. */
export async function listOrganizations(): Promise<Organization[]> {
  const r = await client.get<Organization[]>('/organizations');
  return r.data;
}

export async function createOrganization(body: { name: string; title?: string | null }): Promise<Organization> {
  const r = await client.post<Organization>('/organizations', body);
  return r.data;
}

export async function updateOrganization(
  id: string,
  body: { name?: string | null; title?: string | null; is_active?: boolean | null },
): Promise<Organization> {
  const r = await client.patch<Organization>(`/organizations/${id}`, body);
  return r.data;
}

export async function listOrgMembers(): Promise<OrgMember[]> {
  const r = await client.get<OrgMember[]>('/organizations/members');
  return r.data;
}

export async function addOrgMember(body: { user_id: string; role: string }): Promise<OrgMember> {
  const r = await client.post<OrgMember>('/organizations/members', body);
  return r.data;
}

export async function removeOrgMember(userId: string): Promise<void> {
  await client.delete(`/organizations/members/${userId}`);
}

// ── Active org persistence (localStorage) ──────────────────────────

const ACTIVE_ORG_KEY = 'maven_active_org_id';

export function getActiveOrgId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY);
  } catch {
    return null;
  }
}

export function setActiveOrgId(orgId: string | null): void {
  try {
    if (orgId) localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    else localStorage.removeItem(ACTIVE_ORG_KEY);
  } catch {
    /* ignore */
  }
}

/** Switch the active org. Persists the new value and reloads the page
 * so every cached query refetches against the new org context — same
 * pattern as the office system uses. */
export function switchActiveOrg(orgId: string): void {
  setActiveOrgId(orgId);
  // Also clear the selected company because that company likely belongs
  // to the previous org. The next mount will refetch the new org's
  // companies and re-select the first active one.
  try {
    const stored = localStorage.getItem('hash_company');
    if (stored) {
      const parsed = JSON.parse(stored) as { state?: { selectedCompanyId?: string | null } };
      if (parsed?.state) {
        parsed.state.selectedCompanyId = null;
        localStorage.setItem('hash_company', JSON.stringify(parsed));
      }
    }
  } catch {
    /* ignore */
  }
  window.location.reload();
}
